import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Text } from "@mantine/core";
import { IconCircleCheckFilled } from "@tabler/icons-react";

type LoadingState = {
  text: string;
};

interface MultiStepLoaderProps {
  loadingStates: LoadingState[];
  loading?: boolean;
  duration?: number;
  generationKey?: string | number;
  title?: string;
  subtitle?: string;
  inline?: boolean;
  minimal?: boolean;
  textSize?: string;
}

export function MultiStepLoader({ loadingStates, loading, duration = 2000, generationKey, title = "GTsetu AI is generating your profile", subtitle = "Do not refresh or close this page", inline = false, minimal = false, textSize = "sm" }: MultiStepLoaderProps) {
  const [currentState, setCurrentState] = useState(0);

  // Advance step on interval, looping back to start
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => {
      setCurrentState((prev) => (prev + 1) % loadingStates.length);
    }, duration);
    return () => clearTimeout(timeout);
  }, [currentState, loading, loadingStates.length, duration]);

  // Reset when a new generation starts
  useEffect(() => {
    setCurrentState(0);
  }, [generationKey]);

  return (
    <AnimatePresence mode="wait">
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: inline ? "absolute" : "fixed",
            inset: 0,
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(6px)",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={minimal ? { textAlign: "center" } : {
              background: "#fff",
              borderRadius: 16,
              padding: "32px 40px",
              minWidth: 320,
              maxWidth: 420,
              border: "1px solid #E5E7EB",
              textAlign: "center",
            }}
          >
            {title && (
              <Text fz="md" fw={400} mb={4} c="slate.10">
                {title}
              </Text>
            )}
            {subtitle && (
              <Text fz="xs" c="slate.6" mb="xl">
                {subtitle}
              </Text>
            )}

            {/* Cycling step text — fades out old, fades in new */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentState}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                  <IconCircleCheckFilled size={16} color="#008236" />
                  <Text fz={textSize} fw={400}>
                    {loadingStates[currentState]?.text}
                  </Text>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
